import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useLocationStore } from '@/stores/location-store';
import type { PaginatedResponse } from '@supernova/shared';

/**
 * The same vendor is read through three separate query families — `payees`
 * (dashboard: the All Products vendor filter and the Create PO picker),
 * `sp-payees` (the product form's Assign Vendor list) and `['sp','payees']`
 * (the Send Payments vendor list). Any write has to clear all three.
 *
 * Invalidating only its own family is why a vendor deleted in Send Payments
 * went on being offered in the product and PO pickers: queries default to
 * staleTime 30s, so those lists were served from cache and never refetched.
 */
export function invalidateVendorQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['payees'] });
  queryClient.invalidateQueries({ queryKey: ['sp-payees'] });
  queryClient.invalidateQueries({ queryKey: ['sp', 'payees'] });
}

export function usePayees(
  params: Record<string, string | number | boolean | undefined> = {},
  options: { enabled?: boolean } = {},
) {
  // Auto-inject the active location id so vendor lists are scoped to where
  // the user is. Callers can still override (or pass `locationId: ''` to
  // explicitly opt out for tenant-wide views like super-admin pages).
  const selectedLocationId = useLocationStore((s) => s.selectedLocationId);
  const scopedParams =
    params.locationId === undefined && selectedLocationId ? { ...params, locationId: selectedLocationId } : params;
  return useQuery({
    queryKey: ['payees', scopedParams],
    queryFn: () => api.get<PaginatedResponse<any>>('/payees', scopedParams),
    enabled: options.enabled ?? true,
  });
}

export function usePayee(id: string | undefined) {
  return useQuery({
    queryKey: ['payees', id],
    queryFn: () => api.get<{ data: any }>(`/payees/${id}`),
    enabled: !!id,
  });
}

export function useCreatePayee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.post('/payees', data),
    onSuccess: () => invalidateVendorQueries(queryClient),
  });
}

export function useUpdatePayee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/payees/${id}`, data),
    onSuccess: () => invalidateVendorQueries(queryClient),
  });
}
